import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useAuthCtx, useCartCtx } from '../App';
import { useCurrency } from '../hooks/useCurrency';
import { useRewardConfig } from '../hooks/useRewardConfig';
import api from '../utils/api';
import { toast } from 'react-toastify';
import StripeCheckoutForm from '../components/StripeCheckoutForm';
import { SecureCheckoutBar, GuaranteeMedallions } from '../components/storefront/TrustSignals';
import RewardsLadder from '../components/storefront/RewardsLadder';
import { Icon } from '../components/ui/Icon';

// Card payments run through Stripe. When no Stripe keys are configured the
// backend returns { mock:true } from create-payment-intent and we complete the
// order via the dev mock-pay endpoint so the funnel is fully testable.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const COUNTRIES = [
  { code: 'US', label: 'United States' },
  { code: 'CA', label: 'Canada' },
];

// Glow Cash: every order earns the shopper 1% back into their wallet, redeemable
// next time. Display-only mirror of backend config.cashback.rate — the server is
// authoritative for what's actually credited.
const GLOW_CASH_RATE = 0.01;

export default function CheckoutPage() {
  const { items, total, clearCart, removeItem, coupon: cartCoupon, setCoupon: persistCoupon, clearCoupon } = useCartCtx();
  const { user } = useAuthCtx();
  const { fmt } = useCurrency();
  const rewards = useRewardConfig();
  const navigate = useNavigate();

  useEffect(() => {
    const stale = items.filter(i => !UUID_RE.test(String(i.variantId || '')));
    if (stale.length) {
      stale.forEach(i => removeItem(i.variantId));
      toast.warning(`Removed ${stale.length} outdated item(s) from your cart. Please re-add from the shop.`);
    }
  }, [items, removeItem]);

  const [address, setAddress]           = useState({
    line1: '', city: '', postalCode: '', country: 'US',
  });
  const [agreed, setAgreed] = useState(false);
  // Stripe card payment is the only checkout rail for the storefront.
  const paymentMethod = 'stripe';
  // Set once an order + PaymentIntent exist and we're collecting card details.
  const [stripeSession, setStripeSession] = useState(null); // { clientSecret, publishableKey, orderId }

  // Referral cash (wallet credit) redemption.
  // Caps at the cart subtotal minus any coupon discount — shipping is NEVER
  // covered by referral cash. Backend re-validates the same cap.
  const walletBalance = user?.walletBalanceCents || 0;
  const [creditsToApply, setCreditsToApply] = useState(0);

  const [loading, setLoading] = useState(false);

  // "Very Important Person" accounts ship free on every order.
  const isVip = user?.role === 'vip';
  // Threshold + flat rate are admin-tunable (see /admin/rewards); the backend
  // recomputes authoritatively at checkout.
  const shipping = isVip ? 0 : (total >= rewards.freeShippingThresholdCents ? 0 : rewards.flatShippingRateCents);

  // ─── Coupon code ───────────────────────────────────────────────────
  // Seeded from the cart's persisted coupon so a charm applied in the cart
  // carries straight through to the charge.
  const [couponInput, setCouponInput] = useState(cartCoupon?.code || '');
  const [coupon, setCoupon]           = useState(cartCoupon || null);   // { code, description, discountInCents, freeShipping }
  const [couponError, setCouponError] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  const applyCouponCode = async (rawCode) => {
    const code = (typeof rawCode === 'string' ? rawCode : couponInput).trim().toUpperCase();
    if (!code) return;
    setValidatingCoupon(true);
    setCouponError('');
    try {
      const { data } = await api.post('/orders/validate-coupon', {
        code,
        subtotalInCents: total,
        shippingInCents: shipping,
        scope: 'one_time',
      });
      setCoupon(data);
      persistCoupon({ code: data.code, description: data.description, discountInCents: data.discountInCents, freeShipping: data.freeShipping });
      setCouponInput(data.code);
      toast.success(`Coupon ${data.code} applied!`);
    } catch (err) {
      setCoupon(null);
      clearCoupon();
      setCouponError(err.response?.data?.error || 'Could not apply coupon');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    clearCoupon();
    setCouponInput('');
    setCouponError('');
  };

  // Auto-validate a coupon carried over from the cart, once, against the
  // checkout subtotal (which the backend also re-checks at order creation).
  const autoAppliedRef = useRef(false);
  useEffect(() => {
    if (autoAppliedRef.current) return;
    if (cartCoupon?.code) {
      autoAppliedRef.current = true;
      applyCouponCode(cartCoupon.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartCoupon]);

  const couponDiscount = coupon ? (coupon.freeShipping ? shipping : coupon.discountInCents) : 0;
  const effectiveShipping = coupon?.freeShipping ? 0 : shipping;
  const subtotalAfterCoupon = Math.max(0, total - (coupon?.freeShipping ? 0 : couponDiscount));
  const maxRedeemable = Math.min(walletBalance, subtotalAfterCoupon);
  const clampedCredits = Math.min(creditsToApply, maxRedeemable);
  const finalTotal = Math.max(effectiveShipping, total + effectiveShipping - clampedCredits - (coupon?.freeShipping ? 0 : couponDiscount));

  // Glow Cash earned on this order: 1% of net merchandise spend (subtotal minus
  // coupon discount and any Glow Cash redeemed). Shipping doesn't earn. The
  // backend recomputes the authoritative amount and credits it the moment the
  // order is paid; this is the celebratory preview.
  const glowCashEarned = Math.round(Math.max(0, subtotalAfterCoupon - clampedCredits) * GLOW_CASH_RATE);

  const proceedToPayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/orders/checkout', {
        items: items.map(i => ({ variantId: i.variantId, quantity: i.quantity })),
        shippingAddress: {
          line1:      (address.line1 || '').trim(),
          city:       (address.city || '').trim(),
          postalCode: (address.postalCode || '').trim(),
          country:    (address.country || '').trim().toUpperCase(),
        },
        applyCreditsInCents: Number.isFinite(clampedCredits) ? clampedCredits : 0,
        paymentMethod,
        couponCode: coupon?.code || undefined,
      });
      const orderId = res.data.orderId;
      // Create (or reuse) the Stripe PaymentIntent for this order.
      const pi = await api.post(`/orders/${orderId}/create-payment-intent`);
      if (pi.data.mock) {
        // No Stripe keys configured — complete via the dev mock-pay endpoint.
        await api.post(`/orders/${orderId}/mock-pay`);
        clearCart();
        toast.success('Test payment complete');
        navigate(`/thank-you/${orderId}`);
        return;
      }
      // Live Stripe — collect card details with the PaymentElement.
      setStripeSession({
        clientSecret: pi.data.clientSecret,
        publishableKey: pi.data.publishableKey,
        orderId,
      });
      clearCart();
    } catch (err) {
      const data = err.response?.data;
      const fieldMsg = Array.isArray(data?.fields) && data.fields.length
        ? data.fields.map(f => `${f.field}: ${f.message}`).join('; ')
        : null;
      toast.error(fieldMsg || data?.error || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    navigate('/cart', { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-5xl mx-auto px-4 py-12 w-full">
        <h1 className="text-3xl font-bold mb-2">Checkout</h1>
        <p className="text-sm text-gray-500 mb-6">Prices in USD.</p>

        {/* Rewards ladder — same admin-tunable tiers as the cart, so shoppers
            can still see how close they are to free shipping / gifts at checkout. */}
        <RewardsLadder total={total} freeShippingCoupon={!!coupon?.freeShipping} className="mb-8" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

          {/* Left: address form, or Stripe card form once a live PI exists */}
          <div>
              {stripeSession ? (
                <StripeCheckoutForm
                  clientSecret={stripeSession.clientSecret}
                  publishableKey={stripeSession.publishableKey}
                  orderId={stripeSession.orderId}
                  onCancel={() => setStripeSession(null)}
                />
              ) : (
              <form onSubmit={proceedToPayment} className="card space-y-4">
                <h2 className="font-bold text-xl">Shipping Address</h2>
                {[
                  ['line1',      'Street Address'],
                  ['city',       'City'],
                  ['postalCode', 'Postal Code'],
                ].map(([f, label]) => (
                  <div key={f}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                    <input
                      value={address[f]}
                      onChange={e => setAddress(prev => ({ ...prev, [f]: e.target.value }))}
                      required className="input-field"
                      maxLength={200}
                    />
                  </div>
                ))}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <select
                    value={address.country}
                    onChange={e => setAddress(prev => ({ ...prev, country: e.target.value }))}
                    required
                    className="input-field"
                  >
                    {COUNTRIES.map(c => (
                      <option key={c.code} value={c.code}>{c.label}</option>
                    ))}
                  </select>
                </div>

                <div className="pt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment</label>
                  <div className="border-2 border-tea-600 bg-tea-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 font-semibold">
                      <Icon name="card" className="w-5 h-5 text-tea-700" /> Credit / debit card
                    </div>
                    <p className="text-xs text-gray-500 pl-6 mt-1">
                      Secure card payment via Stripe. You'll enter your card on the next step.
                    </p>
                  </div>
                </div>

                <label className="flex gap-3 items-start text-sm text-gray-700 pt-2">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={e => setAgreed(e.target.checked)}
                    className="mt-1"
                    required
                  />
                  <span>
                    I agree to the <Link to="/terms" className="text-tea-700 underline">Terms of Service</Link> and{' '}
                    <Link to="/privacy" className="text-tea-700 underline">Privacy Policy</Link>.
                  </span>
                </label>

                <button type="submit" disabled={loading || !agreed} className="btn-primary w-full py-3 disabled:opacity-50">
                  {loading ? 'Creating Order…' : 'Continue to Payment'}
                </button>
              </form>
              )}

              {/* Trust signals — accepted cards + secure-checkout guarantee */}
              <SecureCheckoutBar className="mt-4" />
          </div>

          {/* Right: order summary + wallet credit */}
          <div className="card h-fit">
            <h3 className="font-bold text-lg mb-4">Order Summary</h3>
            <div className="space-y-2 text-sm mb-4">
              {items.map(i => (
                <div key={i.variantId} className="flex justify-between">
                  <span className="text-gray-700">{i.productName} × {i.quantity}</span>
                  <span>{fmt(i.priceInCents * i.quantity)}</span>
                </div>
              ))}
            </div>

            {/* Coupon code */}
            {(
              <div className="border-t pt-4 mb-3">
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5"><Icon name="coupons" className="w-5 h-5 text-tea-700" /> Coupon code</div>
                {coupon ? (
                  <div className="bg-tea-50 border border-tea-300 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <div className="font-mono font-bold text-tea-700">{coupon.code}</div>
                      <div className="text-xs text-tea-700">
                        {coupon.freeShipping ? 'Free shipping' : `− ${fmt(coupon.discountInCents)} off`}
                      </div>
                    </div>
                    <button type="button" onClick={removeCoupon}
                      className="text-xs text-red-500 hover:text-red-700 font-medium">Remove</button>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <input type="text" value={couponInput}
                        onChange={e => setCouponInput(e.target.value.toUpperCase())}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyCouponCode(); } }}
                        placeholder="Enter code"
                        className="flex-1 text-sm font-mono uppercase tracking-wider border border-gray-300 rounded-lg px-3 py-2"
                        maxLength={50} />
                      <button type="button" onClick={applyCouponCode} disabled={validatingCoupon || !couponInput.trim()}
                        className="text-sm font-semibold px-4 py-2 rounded-lg border border-tea-300 text-tea-700 hover:bg-tea-50 disabled:opacity-50">
                        {validatingCoupon ? '…' : 'Apply'}
                      </button>
                    </div>
                    {couponError && <p className="text-xs text-red-500 mt-1">{couponError}</p>}
                  </>
                )}
              </div>
            )}

            {(
              <div className="border-t pt-4 mb-3 bg-tea-50/50 rounded-lg p-3">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="inline-flex items-center gap-1.5 font-semibold text-tea-700"><Icon name="commissions" className="w-5 h-5" /> Glow Cash</span>
                  <span className="text-xs text-gray-500">Available: {fmt(walletBalance)}</span>
                </div>
                {walletBalance > 0 ? (
                  <>
                    <div className="flex gap-2 items-center">
                      <input
                        type="range"
                        min="0"
                        max={maxRedeemable}
                        step="100"
                        value={clampedCredits}
                        onChange={e => {
                          const n = parseInt(e.target.value, 10);
                          setCreditsToApply(Number.isFinite(n) ? n : 0);
                        }}
                        className="flex-1"
                        aria-label="Referral cash to apply"
                        disabled={maxRedeemable === 0}
                      />
                      <button
                        type="button"
                        onClick={() => setCreditsToApply(maxRedeemable)}
                        className="text-xs font-semibold text-tea-700 underline whitespace-nowrap disabled:opacity-40 disabled:no-underline"
                        disabled={maxRedeemable === 0}
                      >
                        Max
                      </button>
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      Applying <strong>{fmt(clampedCredits)}</strong> of your referral cash.
                    </div>
                    <div className="text-[11px] text-gray-500 mt-1 italic">
                      Shipping ({fmt(effectiveShipping)}) is not covered by Glow Cash.
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-gray-600">
                    No Glow Cash yet — you'll earn your first <strong>1% back</strong> on this order, ready to spend next time. <Link to="/affiliate" className="text-tea-700 font-semibold underline">Share your referral link</Link> to stack up even more.
                  </div>
                )}
              </div>
            )}

            <div className="border-t pt-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Subtotal</span><span>{fmt(total)}</span>
              </div>
              <div className="flex justify-between">
                <span>Shipping</span>
                <span>{effectiveShipping === 0 ? 'FREE' : fmt(effectiveShipping)}</span>
              </div>
              {coupon && !coupon.freeShipping && couponDiscount > 0 && (
                <div className="flex justify-between text-tea-700">
                  <span>Coupon ({coupon.code})</span>
                  <span>− {fmt(couponDiscount)}</span>
                </div>
              )}
              {clampedCredits > 0 && (
                <div className="flex justify-between text-tea-700">
                  <span>Glow Cash applied</span>
                  <span>− {fmt(clampedCredits)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-base border-t pt-2 mt-2">
                <span>Total (USD)</span><span>{fmt(finalTotal)}</span>
              </div>
            </div>

            {/* Glow Cash earn preview — the fun "what you get back next time" reveal */}
            {glowCashEarned > 0 && (
              <div className="mt-4 relative overflow-hidden rounded-xl border border-tea-300 bg-gradient-to-r from-tea-50 to-pink-50 p-4 text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1 text-tea-700"><Icon name="gift" className="w-6 h-6" /><Icon name="sparkles" className="w-5 h-5" /></div>
                <div className="text-xs uppercase tracking-wider text-tea-700 font-semibold">You'll earn back</div>
                <div className="text-2xl font-extrabold text-tea-700 my-0.5">{fmt(glowCashEarned)} Glow Cash</div>
                <p className="text-xs text-gray-600">
                  That's <strong>1% of this order</strong> dropped straight into your wallet — ready to spend next time you shop.
                </p>
              </div>
            )}

            {/* Guarantee seals */}
            <GuaranteeMedallions className="border-t pt-5 mt-5" />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
